/**
 * Where the developer portal runs and which Pryme environment it talks to.
 *
 * dev.pryme.id is one site for every environment, like a Stripe dashboard with a test/live switch.
 * `/environments.json` lists them (Sandbox → api.sandbox.pryme.id, Production → api.pryme.id). Each
 * environment is a separate deployment with its own sign-in.
 */
import { setApiBase } from '@pryme/ui';

export interface DevEnvironment {
  id: string;
  label: string;
  /** API origin of that deployment. */
  api: string;
  /** End-user portal of that deployment (create a Pryme ID, approvals). */
  portal: string;
  available: boolean;
  note?: string;
}

const ENV_KEY = 'pryme.developers.environment';
let envs: DevEnvironment[] = [];
let current: DevEnvironment | null = null;
let userOrigin = '';

export async function initEnvironments() {
  try {
    const r = await fetch('/environments.json', { cache: 'no-store' });
    if (r.ok) envs = ((await r.json()) as { environments: DevEnvironment[] }).environments ?? [];
  } catch {
    /* fall through: no environment available */
  }
  if (envs.length) {
    const saved = localStorage.getItem(ENV_KEY);
    current = envs.find((e) => e.id === saved && e.available) ?? envs.find((e) => e.available) ?? null;
    if (current) {
      setApiBase(current.api);
      userOrigin = current.portal.replace(/\/$/, '');
    }
  }
}

export const environments = () => envs;
export const currentEnvironment = () => current;

/** Switch environment: another deployment, another session. Reloads the portal. */
export function switchEnvironment(id: string) {
  const e = envs.find((x) => x.id === id);
  if (!e?.available || e.id === current?.id) return;
  localStorage.setItem(ENV_KEY, id);
  localStorage.removeItem('pryme.developers.org'); // organizations are per environment
  location.assign('/');
}

export const userPortalUrl = (path: string) => `${userOrigin}${path}`;

/** The API origin of the current environment (what SDK snippets and the playgrounds call). */
export const apiOrigin = () => (current?.api ?? location.origin).replace(/\/$/, '');

/** True for loopback endpoints: only a local stack (e.g. the test or dev setup) ever hands these out. */
export function isLocalUrl(url: string | undefined | null): boolean {
  if (!url) return false;
  try {
    const h = new URL(url).hostname;
    return h === 'localhost' || h === '127.0.0.1' || h === '[::1]' || h.endsWith('.localhost');
  } catch {
    return false;
  }
}
